/**
 * PARENT→iframe construction-event bridge — the POST side (design
 * `docs/design/live-preview-orchestration.md` §3.G).
 *
 * The Foundry renderer (PARENT) receives `preview:construction-event` over
 * Electron IPC, but the `v1:preview` Ingot's narration strip lives INSIDE
 * the display `<iframe>` (a separate browsing context that never sees the
 * parent's Electron IPC). This hook bridges the gap: it subscribes to
 * `preview:construction-event`, and for each record relays
 * `{ __cruciblePreview: 'construction-event', record }` into the display
 * iframe via `iframe.contentWindow.postMessage(msg, displayOrigin)` — with
 * `targetOrigin = displayOrigin` (the display dev-server origin), NEVER
 * `'*'` (§3.G).
 *
 * It also posts the build-active banner signal
 * `{ __cruciblePreview: 'build-active', active, buildId }` on build-turn
 * start (`active: true`) and done (`active: false`), driven off the chat's
 * `isAiTyping` build/streaming signal (§3.G / §5.4 rough-cut banner).
 *
 * OWNERSHIP: this is the PARENT POST side only. The CHILD consumer — the
 * origin-validating `window.addEventListener('message', …)` listener that
 * checks `event.source === window.parent` AND `event.origin === the Foundry
 * parent origin` — is the `v1:preview` Ingot's job, NOT this hook. This hook
 * does NOT implement the child listener.
 *
 * FLAG GATING: gated on `enabled` (= `flags.livePreview`). Flag-OFF → no
 * subscription is registered and no `postMessage` is ever made →
 * byte-identical to today. The subscription is torn down + the buildId latch
 * cleared on unmount / gameId change.
 *
 * British spelling throughout (behaviour, colour, etc.).
 */

import { useEffect, useRef } from 'react';
import type { ConstructionEvent } from '@volley/crucible-ipc-types/construction-event';
import { useCrucibleClient } from '../transport/context';
import {
  buildBuildActiveMessage,
  buildConstructionEventMessage,
  deriveDisplayOrigin,
  extractBuildId,
  shouldPostToIframe,
  type PreviewBridgeMessage,
} from '../workspace/preview-orch-bridge';

export interface PreviewConstructionBridgeOptions {
  /** `flags.livePreview` — the feature gate. Flag-OFF: no subscription, no
   *  postMessage (byte-identical). */
  enabled: boolean;
  /** The active game whose display iframe this bridge owns. The
   *  subscription is re-bound when this changes (per-gameId guard). */
  gameId: string | null | undefined;
  /** Ref to the live display `<iframe>` (the postMessage target). */
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  /** The display preview URL — its origin is the `targetOrigin`. */
  displayUrl: string | null | undefined;
  /** The display URL whose iframe load event has completed. */
  loadedDisplayUrl?: string | null | undefined;
  /** `chat.isAiTyping` — the build/streaming signal that drives the
   *  build-active banner (true on build-turn start, false on done/error). */
  isAiTyping: boolean;
}

export function usePreviewConstructionBridge(options: PreviewConstructionBridgeOptions): void {
  const client = useCrucibleClient();
  const { enabled, gameId, iframeRef, displayUrl, loadedDisplayUrl, isAiTyping } = options;

  // The latest `buildId` seen from a `session-begin` / `session-attached`
  // record — the canonical build identifier (§3.A). The renderer has no
  // other direct source for it, so we latch it off the construction-event
  // stream and reuse it for the build-active banner payload. Falls back to
  // the gameId so the banner always has a stable identifier even before the
  // first session marker lands.
  const buildIdRef = useRef<string | null>(null);

  // Mirror the live inputs into refs so the long-lived `preview:construction-
  // event` handler (registered once per gameId) reads CURRENT values without
  // re-subscribing on every displayUrl / isAiTyping change (a churny
  // re-subscribe would drop+replay records, double-arming the main watcher).
  const displayUrlRef = useRef(displayUrl);
  const loadedDisplayUrlRef = useRef(loadedDisplayUrl);
  const gameIdRef = useRef(gameId);
  const enabledRef = useRef(enabled);
  const loggedPostMessageErrorUrlsRef = useRef(new Set<string>());
  displayUrlRef.current = displayUrl;
  loadedDisplayUrlRef.current = loadedDisplayUrl;
  gameIdRef.current = gameId;
  enabledRef.current = enabled;

  // Helper: post one bridge message into the display iframe, honouring the
  // per-gameId guard + the `targetOrigin = displayOrigin` (never '*') rule.
  // `boundGameId` is the gameId the subscription was registered FOR; the
  // guard rejects a post when it has diverged from the now-active game.
  const post = (msg: PreviewBridgeMessage, boundGameId: string): void => {
    const displayOrigin = deriveDisplayOrigin(displayUrlRef.current);
    const contentWindow = iframeRef.current?.contentWindow ?? null;
    if (
      !shouldPostToIframe({
        enabled: enabledRef.current,
        boundGameId,
        activeGameId: gameIdRef.current,
        displayOrigin,
        displayUrl: displayUrlRef.current,
        loadedDisplayUrl: loadedDisplayUrlRef.current,
        hasContentWindow: contentWindow !== null,
      })
    ) {
      return;
    }
    // `displayOrigin` is non-null here (shouldPostToIframe gates on it).
    try {
      contentWindow!.postMessage(msg, displayOrigin!);
    } catch (err) {
      const displayUrlKey = displayUrlRef.current ?? 'unknown-display-url';
      if (!loggedPostMessageErrorUrlsRef.current.has(displayUrlKey)) {
        loggedPostMessageErrorUrlsRef.current.add(displayUrlKey);
        console.debug?.('[preview-bridge] postMessage failed', {
          displayUrl: displayUrlKey,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
  };

  // ── Construction-event subscription (re-bound per gameId) ──────────────
  //
  // Per-gameId guard, two layers: (1) the `preview:construction-event` push
  // payload is a BARE `ConstructionEvent` (the gameId is dropped on the wire
  // — main's `pushEvent` forwards only the record), and main's watcher is
  // SINGLE-active-game-scoped: `startConstructionLogWatch(gameId)` /
  // `stopConstructionLogWatch(gameId)` are wired to the dev session, so main
  // only ever tails + pushes for the live game (§3.E / §6.4). (2) On the
  // renderer side this effect re-binds per gameId — React tears down the old
  // listener synchronously BEFORE arming the new one, so there is no
  // renderer-side overlap window — and every `post` re-checks
  // `boundGameId === activeGameId` (`shouldPostToIframe`) so a record can
  // never land in a different game's iframe even if a stale closure fires.
  useEffect(() => {
    // FLAG-OFF / no game → no subscription, no postMessage (byte-identical).
    if (!enabled || !gameId) return;

    // Clear the prior game's buildId latch so a stale buildId can't leak
    // into the new game's build-active payload.
    buildIdRef.current = null;

    const boundGameId = gameId;

    const unsub = client.subscribe('preview:construction-event', (record: ConstructionEvent) => {
      // Latch the canonical buildId off the session markers for the
      // build-active payload (the renderer has no other source).
      const seen = extractBuildId(record);
      if (seen !== null) buildIdRef.current = seen;
      // Relay the record verbatim into the display iframe.
      post(buildConstructionEventMessage(record), boundGameId);
    });

    return () => {
      unsub();
      // Clear the latch on teardown so a re-arm for a new game starts clean.
      buildIdRef.current = null;
    };
    // NOTE: intentional narrow deps — only the gameId + flag re-bind the
    // subscription; `post` is a stable closure over refs and `iframeRef` is
    // a stable ref object. (The `react-hooks` ESLint plugin is not enabled
    // in this repo's flat config — a `react-hooks/exhaustive-deps` disable
    // directive would itself error, so the intent is documented here.)
  }, [client, enabled, gameId]);

  // ── Build-active banner signal (driven off isAiTyping) ─────────────────
  // Posts `active: true` on build-turn start and `active: false` on
  // done/error. Effect re-runs on every isAiTyping flip; the per-gameId
  // guard + the flag gate live inside `post`.
  //
  // `displayUrl` + `loadedDisplayUrl` are ALSO deps so the CURRENT build-active state is re-posted
  // when the display iframe first becomes ready — the auto-open race where a
  // build turn is already mid-flight (`isAiTyping === true`) BEFORE the iframe
  // mounts: without this the iframe would never receive `active: true`
  // because `isAiTyping` didn't change when the URL arrived. `post` no-ops
  // until the iframe has a `contentWindow`, so the re-post lands once it does.
  useEffect(() => {
    if (!enabled || !gameId) return;
    const buildId = buildIdRef.current ?? gameId;
    post(buildBuildActiveMessage(isAiTyping, buildId), gameId);
    // NOTE: intentional narrow deps (see the construction-event effect above
    // for why a `react-hooks/exhaustive-deps` disable directive can't be used
    // in this repo). `post` reads CURRENT displayUrl / iframe via refs; the
    // dep list is the SET OF TRIGGERS that should re-emit the banner state.
  }, [enabled, gameId, isAiTyping, displayUrl, loadedDisplayUrl]);
}
